import { supabase } from '@/lib/supabase';
import {
  Laptop,
  LaptopInsert,
  LaptopUpdate,
  LaptopStatusHistory,
  PaginatedResult,
} from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchLaptopsParams {
  search?: string;
  status?: string;
  brand?: string;
  condition?: string;
  archiveFilter?: 'active' | 'archived' | 'all';
  page?: number;
  pageSize?: number;
  fetchAll?: boolean;
  isStaff?: boolean;
}

export async function fetchLaptops(
  params: FetchLaptopsParams
): Promise<PaginatedResult<Laptop>> {
  const {
    search = '',
    status = '',
    brand = '',
    condition = '',
    archiveFilter = 'active',
    page = 0,
    pageSize = PAGE_SIZE,
    fetchAll = false,
    isStaff = false,
  } = params;

  if (isStaff) {
    const { data: rpcData, error: rpcErr } = await supabase.rpc('get_inventory_catalog');
    if (rpcErr) throw rpcErr;
    // This RPC deliberately returns active inventory without financial columns.
    // A failure must never fall through to the unrestricted table query below.
    if (archiveFilter === 'archived') return { data: [], count: 0 };
    let filtered = (rpcData as Laptop[]) || [];
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.stock_id?.toLowerCase().includes(s) ||
          item.serial_number?.toLowerCase().includes(s) ||
          item.service_tag?.toLowerCase().includes(s) ||
          item.brand?.toLowerCase().includes(s) ||
          item.model?.toLowerCase().includes(s) ||
          item.cpu?.toLowerCase().includes(s)
      );
    }
    if (!status || status === 'current_stock') {
      filtered = filtered.filter((i) => !['sold', 'scrapped'].includes(i.status));
    } else if (status !== 'all') {
      filtered = filtered.filter((i) => i.status === status);
    }
    if (brand) filtered = filtered.filter((i) => i.brand === brand);
    if (condition) filtered = filtered.filter((i) => i.condition === condition);

    if (fetchAll) {
      return { data: filtered, count: filtered.length };
    }

    const from = page * pageSize;
    const pageData = filtered.slice(from, from + pageSize);
    return { data: pageData, count: filtered.length };
  }

  let query = supabase
    .from('laptops')
    .select('*, supplier:suppliers(id, name)', { count: 'exact' })
    .order('created_at', { ascending: false });

  if (archiveFilter === 'active') {
    query = query.is('archived_at', null);
  } else if (archiveFilter === 'archived') {
    query = query.not('archived_at', 'is', null);
  }

  if (search.trim()) {
    const s = search.trim();
    // schema columns: stock_id, serial_number, service_tag, brand, model, cpu
    query = query.or(
      `stock_id.ilike.%${s}%,serial_number.ilike.%${s}%,service_tag.ilike.%${s}%,brand.ilike.%${s}%,model.ilike.%${s}%,cpu.ilike.%${s}%`
    );
  }

  // Status filter handling:
  // - 'current_stock' (or default empty): excludes 'sold' and 'scrapped'
  // - 'all': returns all statuses without status filtering
  // - specific status (e.g. 'in_stock', 'sold', 'reserved', etc.): filters strictly to that status
  if (!status || status === 'current_stock') {
    query = query.not('status', 'in', '("sold","scrapped")');
  } else if (status !== 'all') {
    query = query.eq('status', status);
  }

  if (brand) query = query.eq('brand', brand);
  if (condition) query = query.eq('condition', condition);

  if (!fetchAll) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);
  }

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as Laptop[], count: count ?? (data?.length ?? 0) };
}

export async function fetchLaptopById(id: string): Promise<Laptop> {
  const { data, error } = await supabase
    .from('laptops')
    .select('*, supplier:suppliers(id, name)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Laptop;
}

export async function fetchLaptopHistory(laptopId: string): Promise<LaptopStatusHistory[]> {
  const { data, error } = await supabase
    .from('laptop_status_history')
    .select('*')
    .eq('laptop_id', laptopId)
    .order('changed_at', { ascending: false });

  if (error) return [];
  return (data ?? []) as LaptopStatusHistory[];
}

export async function fetchAvailableLaptops(search = '', isStaff = false): Promise<Laptop[]> {
  if (isStaff) {
    const { data, error } = await supabase.rpc('get_inventory_catalog');
    if (error) throw error;
    const term = search.trim().toLowerCase();
    return ((data ?? []) as Laptop[]).filter(laptop =>
      ['in_stock', 'reserved'].includes(laptop.status) && (!term ||
        [laptop.stock_id, laptop.serial_number, laptop.service_tag, laptop.brand, laptop.model, laptop.cpu]
          .some(value => value?.toLowerCase().includes(term))));
  }
  let query = supabase
    .from('laptops')
    .select('id, stock_id, serial_number, brand, model, cpu, ram, storage, gpu, screen_size, touchscreen, asking_price, minimum_price, landed_cost, current_cost_basis, condition, status')
    .is('archived_at', null)
    .in('status', ['in_stock', 'reserved'])
    .order('created_at', { ascending: false })
    .limit(200);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`stock_id.ilike.%${s}%,serial_number.ilike.%${s}%,brand.ilike.%${s}%,model.ilike.%${s}%,cpu.ilike.%${s}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Laptop[];
}

export async function setGroupAskingPriceRpc(params: {
  brand: string;
  model: string;
  cpu?: string | null;
  touchscreen?: boolean | null;
  asking_price: number;
  reason?: string;
}): Promise<{
  success: boolean;
  updated_count: number;
  skipped_count: number;
  new_asking_price: number;
}> {
  const { data, error } = await supabase.rpc('set_group_asking_price', {
    p_brand: params.brand,
    p_model: params.model,
    p_cpu: params.cpu ?? null,
    p_touchscreen: params.touchscreen ?? null,
    p_asking_price: params.asking_price,
    p_reason: params.reason || 'Bulk group asking price update',
  });

  if (error) throw error;
  return data as {
    success: boolean;
    updated_count: number;
    skipped_count: number;
    new_asking_price: number;
  };
}

export async function createLaptop(payload: LaptopInsert): Promise<Laptop> {
  if (payload.status === 'sold') {
    throw new Error('Laptops cannot be manually marked as sold. Use the Sales module.');
  }

  const insertPayload = { ...payload };
  if (!insertPayload.stock_id || !insertPayload.stock_id.trim()) {
    delete insertPayload.stock_id;
  }

  const { data, error } = await supabase
    .from('laptops')
    .insert(insertPayload)
    .select('*, supplier:suppliers(id, name)')
    .single();

  if (error) throw error;
  return data as Laptop;
}

export async function updateLaptop(id: string, payload: LaptopUpdate): Promise<Laptop> {
  const { data: existing } = await supabase.from('laptops').select('status').eq('id', id).single();
  if (existing) {
    if (existing.status === 'sold' && payload.status && payload.status !== 'sold') {
      throw new Error('A sold laptop cannot be transitioned to another status via edit. Use the Returns module.');
    }
    if (existing.status !== 'sold' && payload.status === 'sold') {
      throw new Error('Laptops cannot be manually marked as sold. Use the Sales module.');
    }
  }

  const safePayload = { ...payload };
  if (existing?.status === 'sold') {
    delete safePayload.status;
  }

  const { data, error } = await supabase
    .from('laptops')
    .update(safePayload)
    .eq('id', id)
    .select('*, supplier:suppliers(id, name)')
    .single();

  if (error) throw error;
  return data as Laptop;
}

export function generateLaptopsCsv(laptops: Laptop[], includeCost = false): string {
  const headers = [
    'Stock ID',
    'Brand',
    'Model',
    'Serial Number',
    'Service Tag',
    'CPU',
    'RAM',
    'Storage',
    'GPU',
    'Condition',
    'Battery Health (%)',
    'Status',
    'Asking Price (PKR)',
  ];

  if (includeCost) {
    headers.push('Purchase Price', 'Landed Cost', 'Refurbishment Cost');
  }

  const rows = laptops.map((l) => {
    const row = [
      `"${l.stock_id || ''}"`,
      `"${l.brand || ''}"`,
      `"${l.model || ''}"`,
      `"${l.serial_number || ''}"`,
      `"${l.service_tag || ''}"`,
      `"${l.cpu || ''}"`,
      `"${l.ram || ''}"`,
      `"${l.storage || ''}"`,
      `"${l.gpu || ''}"`,
      `"${l.condition || ''}"`,
      l.battery_health ?? '',
      `"${l.status || ''}"`,
      l.asking_price ?? 0,
    ];
    if (includeCost) {
      row.push(l.purchase_price ?? 0, l.landed_cost ?? 0, l.refurbishment_cost ?? 0);
    }
    return row.join(',');
  });

  return [headers.join(','), ...rows].join('\n');
}

export async function removeLaptopFromStockRpc(params: {
  laptopId: string;
  reason: string;
}): Promise<{
  success: boolean;
  laptop_id: string;
  stock_id: string;
  status: string;
  message: string;
}> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required to remove a laptop from stock.');
  }

  const { data, error } = await supabase.rpc('remove_laptop_from_stock', {
    p_laptop_id: params.laptopId,
    p_reason: params.reason.trim(),
  });

  if (error) throw error;
  return data as {
    success: boolean;
    laptop_id: string;
    stock_id: string;
    status: string;
    message: string;
  };
}

export async function archiveLaptopRpc(params: {
  laptopId: string;
  reason: string;
}): Promise<{
  success: boolean;
  laptop_id: string;
  stock_id: string;
  status: string;
  message: string;
}> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required to archive a laptop.');
  }

  const { data, error } = await supabase.rpc('archive_laptop', {
    p_laptop_id: params.laptopId,
    p_reason: params.reason.trim(),
  });

  if (error) throw error;
  return data as {
    success: boolean;
    laptop_id: string;
    stock_id: string;
    status: string;
    message: string;
  };
}

export async function unarchiveLaptopRpc(laptopId: string): Promise<{
  success: boolean;
  laptop_id: string;
  stock_id: string;
  status: string;
  message: string;
}> {
  const { data, error } = await supabase.rpc('unarchive_laptop', {
    p_laptop_id: laptopId,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    laptop_id: string;
    stock_id: string;
    status: string;
    message: string;
  };
}

export interface ReconfigureInStockLaptopParams {
  laptop_id: string;
  reason: string;
  installed_components?: Array<{
    component_type: 'ram' | 'storage';
    accessory_id: string;
    quantity: number;
  }>;
  recovered_components?: Array<{
    component_type: 'ram' | 'storage';
    accessory_id?: string | null;
    name?: string | null;
    category?: string | null;
    quantity: number;
    unit_cost: number;
  }>;
  new_ram?: string | null;
  new_storage?: string | null;
  clear_ram?: boolean;
  clear_storage?: boolean;
}

export async function reconfigureInStockLaptopRpc(
  params: ReconfigureInStockLaptopParams
): Promise<{
  success: boolean;
  laptop_id: string;
  stock_id: string;
  old_cost_basis: number;
  new_cost_basis: number;
  installed_cost_total: number;
  recovered_cost_total: number;
  ram: string | null;
  storage: string | null;
}> {
  const { data, error } = await supabase.rpc('reconfigure_in_stock_laptop', {
    p_laptop_id: params.laptop_id,
    p_reason: params.reason,
    p_installed_components: params.installed_components ?? [],
    p_recovered_components: params.recovered_components ?? [],
    p_new_ram: params.new_ram ?? null,
    p_new_storage: params.new_storage ?? null,
    p_clear_ram: params.clear_ram ?? false,
    p_clear_storage: params.clear_storage ?? false,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    laptop_id: string;
    stock_id: string;
    old_cost_basis: number;
    new_cost_basis: number;
    installed_cost_total: number;
    recovered_cost_total: number;
    ram: string | null;
    storage: string | null;
  };
}

